import i18next from 'i18next';
import { useSubscribableSetKinderConnectCaptureRequiredMutation } from '@famly/stat_kinder-connect-capture_data';
import { useUpdateError, useUpdateSuccess } from '@famly/stat_ts-utils_global-messages';
import { useKinderConnectCaptureSettingsChildrenList } from './settings/use-kinder-connect-capture-settings-children-list';
/**
 * Returns a submit handler for the KinderConnect capture settings form.
 *
 * Diffs the submitted `enabledChildIds` against the site's fetched capture flags (an Apollo
 * cache hit, since the list already ran the same query) and sends only the children whose
 * setting changed -- the backend caps a request at 200 changes, so resending the unchanged ones
 * would count against that limit for nothing. Refuses to submit when that baseline isn't loaded,
 * rather than treating a missing baseline as "nothing changed" and silently dropping the user's
 * toggles.
 *
 * @param options - Provides `variables.siteId`, the site to diff the submission against.
 * @param onClose - Called to dismiss the modal once the mutation completes.
 * @returns A function that accepts the completed form values and saves the changed settings.
 */
export const useKinderConnectCaptureSettingsSubmit = (options, onClose) => {
    var _a;
    const { data } = useKinderConnectCaptureSettingsChildrenList((_a = options.variables.siteId) !== null && _a !== void 0 ? _a : '');
    const updateSuccess = useUpdateSuccess();
    const updateError = useUpdateError();
    const [setCaptureRequired] = useSubscribableSetKinderConnectCaptureRequiredMutation({
        onCompleted: () => {
            updateSuccess(i18next.t($ => $.stat.kinderConnectCapture.businessLogic.src.translations.json.settings.saveSuccess));
            onClose === null || onClose === void 0 ? void 0 : onClose();
        },
    });
    return async (formValues) => {
        if (!data) {
            updateError(i18next.t($ => $.stat.kinderConnectCapture.businessLogic.src.translations.json.settings.saveError));
            return;
        }
        const enabledChildIds = new Set(formValues.enabledChildIds);
        const changedSettings = data.children
            .filter(child => enabledChildIds.has(child.id) !== child.captureEnabled)
            .map(child => ({ childId: child.id, captureRequired: enabledChildIds.has(child.id) }));
        if (changedSettings.length === 0) {
            onClose === null || onClose === void 0 ? void 0 : onClose();
            return;
        }
        await setCaptureRequired({ variables: { settings: changedSettings } });
    };
};
